import styled from "styled-components";
import { CorzTextArea } from "../../components/form/TextField";
import { useEffect, useRef, useState } from "react";
import {
	FilledButton,
	OutlinedButton,
} from "../../components/buttons/CommonButton";
import {
	collection,
	onSnapshot,
	orderBy,
	query,
	Timestamp,
	where,
} from "firebase/firestore";
import { auth, firestore, userData } from "../../../App";
import { timestampToDate } from "../../../tools/DateTool";
import { deleteNote, pushNote, updateNote } from "../../../tools/FirestoreTool";
import ModalDialogComponent from "../../components/dialog/ModalDialog";
import { eventEmitter } from "../../../tools/EventEmitter";

const Container = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	padding: 12px 24px;
	margin-top: 12px;
	-webkit-user-select: none;
	user-select: none;

	h2 {
		margin-bottom: 12px;
	}

	@media screen and (max-width: 768px) {
		padding: 12px;
	}

	@media screen and (max-width: 400px) {
		padding: 12px 6px;
	}
`;

const ButtonContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

const NotesContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-top: 12px;
`;

const NoteContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 6px 12px;
	border-bottom: 1px solid var(--color-outline);

	&:last-child {
		border-bottom: none;
	}
`;

const Header = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 3px;
	color: var(--color-tertiary);
`;

const Author = styled.h3`
	color: var(--color-tertiary);
`;

const DeleteButton = styled.span`
	position: absolute;
	top: 0;
	right: 0;
	width: 24px;
	height: 24px;
	color: var(--color-error);
	font-family: "Material Symbols Rounded" !important;
	font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24;
	font-size: 24px;
	line-height: 24px;
	cursor: pointer;
	-webkit-user-select: none;
	user-select: none;
	transition: 0.3s;
	z-index: 1;

	&::before {
		position: absolute;
		content: "";
		top: -6px;
		right: -6px;
		width: 36px;
		height: 36px;
		border-radius: 50%;
		background: transparent;
		z-index: -1;
		transition: 0.3s;
	}

	&:hover:before {
		background: var(--color-surface-container-highest);
	}
`;

const ReplayContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 6px;
	border-left: 3px solid var(--color-tertiary);
	margin-top: 12px;
	border-radius: 6px;
	padding: 6px 12px;
	background: var(--color-surface-container-high);
	cursor: pointer;

	&::before {
		content: "close";
		position: absolute;
		top: calc(50% - 12px);
		right: 12px;
		width: 24px;
		height: 24px;
		line-height: 24px;
		font-size: 24px;
		font-family: "Material Symbols Rounded";
		color: var(--color-on-surface);
	}
`;

function NoteViewPager() {
	const [localUserData, setLocalUserData] = useState(userData);
	const [noteData, setNoteData] = useState([]);
	const [userName, setUserName] = useState("");
	const [replay, setReplay] = useState({});
	const [deletingNoteId, setDeletingNoteId] = useState(null);
	const [noteQuery, setNoteQuery] = useState(null);

	const textAreaRef = useRef(null);
	const dialogRef = useRef(null);

	const noteRef = collection(firestore, "notes");

	useEffect(() => {
		async function fetchNote() {
			await onSnapshot(
				noteQuery,
				{ includeMetadataChanges: true },
				(snapshot) => {
					let notes = [...noteData];

					snapshot.docChanges().forEach((change) => {
						console.log("Added notes: " + change.doc.data());

						if (change.type === "added") {
							if (
								!notes.some((note) => note.id === change.doc.id)
							) {
								notes.push({
									id: change.doc.id,
									...change.doc.data(),
								});
							}
						} else if (change.type === "modified") {
							console.log("Modified notes: " + change.doc.data());

							const index = notes.findIndex(
								(note) => note.id !== change.doc.id
							);
							if (index !== -1) {
								notes[index] = {
									...notes[index],
									...change.doc.data(),
								};
							}
						} else if (change.type === "removed") {
							console.log("Removed notes: " + change.doc.data());

							notes = notes.filter(
								(note) => note.id !== change.doc.id
							);
						}
					});

					setNoteData(notes);
				},
				(error) => {
					console.error(error);
				}
			);
		}

		if (noteQuery) fetchNote();

		const handleUserDataUpdated = (data) => {
			setLocalUserData(data);
		};

		eventEmitter.on("userDataUpdated", handleUserDataUpdated);

		return () => {
			eventEmitter.off("userDataUpdated", handleUserDataUpdated);
		};
	}, [noteQuery]);

	useEffect(() => {
		if (localUserData) {
			setUserName(localUserData.nome);

			
			let constraints = [orderBy("timestamp", "desc")];
			if (localUserData.uid !== "I0yR09VGscWRA56pIa7jfIlRQsR2") {
				constraints = [
					where("uid", "!=", "I0yR09VGscWRA56pIa7jfIlRQsR2"),
					...constraints,
				];
			}

			setNoteQuery(query(noteRef, ...constraints));
		}
	}, [localUserData]);

	const handleSendNote = () => {
		const text = textAreaRef.current.getValue().trim();

		if (text === "") {
			textAreaRef.current.updateError(true, "Riempi questo campo");
			return;
		}

		const myUid = auth.currentUser.uid;
		const timestamp = Timestamp.fromDate(new Date());

		const data = {
			text: text,
			timestamp: timestamp,
			reactionist: [],
			nome: userName,
			uid: myUid,
		};

		pushNote(data);

		setReplay({});
	};

	const handleReaction = (noteId) => {
		const myUid = auth.currentUser.uid;
		const noteDoc = noteData.find((note) => note.id === noteId);

		if (noteDoc === null) return;

		const reactionData = [...noteData.reactionist];

		if (noteDoc.reactionist.includes(myUid)) {
			reactionData.filter((uid) => uid !== myUid);
		} else {
			reactionData.push(myUid);
		}

		updateNote(noteId, {
			reactionist: reactionData,
		});
	};

	const handleDeleteNote = (docId) => {
		setDeletingNoteId(docId);
		dialogRef.current.open();
	};

	const handleDeleteNoteReally = () => {
		deleteNote(deletingNoteId);
	};

	return (
		<Container>
			<h2>Note</h2>
			<p>
				Qui puoi aggiungere, rispondere o reagire alle note. Le note
				sono utili per segnalare eventuali problemi o suggerire
				sostituzioni. Puoi anche ricevere notifiche per rimanere
				aggiornato.
			</p>
			{replay.id && (
				<ReplayContainer
					onClick={() => {
						setReplay({});
					}}
				>
					<Author>{replay.author}</Author>
					<p>{replay.text}</p>
				</ReplayContainer>
			)}
			<CorzTextArea
				ref={textAreaRef}
				id="note"
				label="Scrivi una nota..."
				limited={false}
			></CorzTextArea>
			<ButtonContainer>
				<FilledButton text={"Invia"} onClick={handleSendNote}>
					<span className="material-symbol fill">send</span>
				</FilledButton>
			</ButtonContainer>
			<NotesContainer>
				{noteData.map((note, index) => (
					<NoteContent key={index}>
						<Header>
							<span className="title-small">
								{timestampToDate(note.timestamp)}
								{note.eventOf && (
									<span className="title-small">
										&nbsp;-&nbsp;{note.eventOf}
									</span>
								)}
							</span>
							<Author>{note.nome}</Author>
							{note.uid === localUserData.uid && (
								<DeleteButton
									onClick={() => handleDeleteNote(note.id)}
								>
									delete
								</DeleteButton>
							)}
						</Header>
						<p>{note.text}</p>
						<ButtonContainer>
							<FilledButton
								text={"Rispondi"}
								isDisabled={true}
								onClick={() => {
									setReplay({
										id: note.id,
										author: note.nome,
										text: note.text,
									});
								}}
							>
								<span className="material-symbol fill">
									reply
								</span>
							</FilledButton>
							<OutlinedButton
								selected={
									note.reactionist
										? note.reactionist.find(
												(uid) => uid === localUserData.uid
										  )
										: false
								}
								text={
									"👍 " +
									(note.reactionist
										? note.reactionist.length
										: 0)
								}
								onClick={() => handleReaction(note.id)}
							></OutlinedButton>
						</ButtonContainer>
					</NoteContent>
				))}
			</NotesContainer>
			<ModalDialogComponent
				ref={dialogRef}
				title="Elimina nota"
				icon="delete"
				theme="var(--color-error)"
				cancelButton={{
					text: "Annulla",
					theme: "error",
					onClick: () => dialogRef.current.close(),
				}}
				confirmButton={{
					text: "Elimina",
					theme: "error",
					onClick: handleDeleteNoteReally,
				}}
			>
				<p className="body-medium">
					Sei sicuro di voler eliminare la nota?
				</p>
			</ModalDialogComponent>
		</Container>
	);
}

export default NoteViewPager;
